import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import { AgentVerificationModal } from '../../AgentVerificationDetails.types'
import { useAgentVerificationDecisionContext } from '../../useAgentVerificationDecisionContext/useAgentVerificationDecisionContext'

import { AgentVerificationStatus, Validation } from '@percent/admin-dashboard/api/types'
import { PercentButton } from '@percent/admin-dashboard/common/components'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { Alert, Button, CardDashboard, Flex, Tabs, Typography, TabProps } from '@percent/lemonade'
import styles from './AgentVerificationDecision.module.scss'
import { AgentVerificationDecisionProps } from './AgentVerificationDecision.types'
import { AgentVerificationApproveEmailModal } from './agentVerificationApproveEmailModal/AgentVerificationApproveEmailModal'
import { AgentVerificationApproveModalContainer } from './agentVerificationApproveModal/AgentVerificationApproveModalContainer'
import { AgentVerificationMarkAsSafeModal } from './agentVerificationMarkAsSafeModal/AgentVerificationMarkAsSafeModal'
import { AgentVerificationRejectModalContainer } from './agentVerificationRejectModal/AgentVerificationRejectModalContainer'
import { AgentVerificationUpdateStatusModal } from './agentVerificationUpdateStatusModal/AgentVerificationUpdateStatusModal'
import { useQuery } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useRequiredParams } from '@percent/admin-dashboard/common/hooks'

type ValidationRequestStatus = 'pending' | 'accepted' | 'rejected' | null
const getValidationRequestStatus = (validation: Validation | null): ValidationRequestStatus => {
  if (!validation) return null

  if (validation.acceptedAt) return 'accepted'

  if (validation.rejectedAt) return 'rejected'

  return 'pending'
}

const readOnlyStatuses = ['pending', 'rejected'] as const

const checkReadOnlyStatus = (status: ValidationRequestStatus): status is typeof readOnlyStatuses[number] =>
  readOnlyStatuses.includes(status as typeof readOnlyStatuses[number])

export function AgentVerificationDecision({
  agentFullName,
  organisationName,
  isLoading,
  errorMessage,
  approveEmail,
  refresh,
  fraudCheckResultsRefresh,
  agentStatus,
  hasFailingFraudFlags,
  allowedOrganisations,
  partnerName,
  isStatusDynamic,
  activeTab,
  setActiveTab,
  similarApplicationsAmount,
  matchedDocumentsAmount,
  fraudCheckResults,
  isAutoFraudCheckEnabled,
  isOrgDataAvailable,
  validationRequestId,
  hasInboxVerificationEmailSentLog
}: Readonly<AgentVerificationDecisionProps>) {
  const { t } = useTranslation()
  const { setOpenedModal } = useAgentVerificationDecisionContext()
  const { id } = useRequiredParams()
  const location = useLocation()
  const dynamicAgentVerificationFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_DYNAMIC_AGENT_VERIFICATION)
  const triggerEmailVerificationFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_TRIGGER_EMAIL_VERIFICATION)
  const { adminService } = useServices()

  useEffect(() => {
    if (location) {
      setActiveTab(`${location.pathname}`)
    }
  }, [location, setActiveTab])

  const triggerEmailVerificationButton = () => {
    approveEmail({ id })
    setOpenedModal(AgentVerificationModal.APPROVE_EMAIL)
  }

  const { data } = useQuery(
    ['/validation-requests', validationRequestId],
    adminService.getValidationRequest,
    { id: validationRequestId },
    { enabled: !!validationRequestId }
  )

  const status = getValidationRequestStatus(data)
  const isPendingAgentVerification = [AgentVerificationStatus.PENDING, AgentVerificationStatus.PENDING_REVIEW].includes(
    agentStatus as AgentVerificationStatus
  )

  const tabs: TabProps['items'] = [
    { key: `/agent-verification/${id}`, label: t('typography.applicationDetails') },
    {
      key: `/agent-verification/${id}/other`,
      label: t('typography.otherApplications', { amount: similarApplicationsAmount })
    },
    {
      key: `/agent-verification/${id}/duplicate-documents`,
      label: t('typography.matchedDocuments', { amount: matchedDocumentsAmount })
    },
    { key: `/agent-verification/${id}/timeline`, label: t('typography.applicationTimeline') }
  ]

  const handleMarkAsSafe = () => setOpenedModal(AgentVerificationModal.MARK_AS_SAFE)

  const markAsSafeRefresh = () => {
    refresh()
    fraudCheckResultsRefresh()
  }

  const hasAnyFraudFlags = !!fraudCheckResults && fraudCheckResults.checkResults.length > 0
  const canMarkAsSafe =
    isAutoFraudCheckEnabled &&
    isPendingAgentVerification &&
    isOrgDataAvailable &&
    fraudCheckResults?.result === 'needs_review' &&
    !fraudCheckResults.isMarkedAsSafe

  return (
    <>
      <CardDashboard className={styles.decisionContainer}>
        {fraudCheckResults?.result === 'fail' && (
          <div className={styles.fraudAlertWrapper}>
            <Alert title={t('dialog.autoFraud.fail')} variant="error" />
          </div>
        )}
        {!hasFailingFraudFlags && hasAnyFraudFlags && (
          <div className={styles.fraudAlertWrapper}>
            <Alert title={t('dialog.autoFraud.pass')} variant="success" />
          </div>
        )}
        {fraudCheckResults?.isMarkedAsSafe && isPendingAgentVerification && (
          <div className={styles.fraudAlertWrapper}>
            <Alert title={t('dialog.autoFraud.markedAsSafe')} variant="success" />
          </div>
        )}
        {canMarkAsSafe && (
          <div className={styles.fraudAlertWrapper}>
            <Alert title={t('autoFraudChecks.fraudFlags.reviewAlert')} variant="info" />
          </div>
        )}
        <Flex direction="column" gap={16}>
          {!isOrgDataAvailable && checkReadOnlyStatus(status) && (
            <Alert variant="info" title={t('dialog.noOrgData.title')}>
              <Typography>{t(`dialog.noOrgData.${status}.description`)}</Typography>
              <Link to={`/validations/${validationRequestId}`}>{t('dialog.noOrgData.link')}</Link>
            </Alert>
          )}
          <div className={styles.contentWrapper}>
            <div className={styles.organisationInfoWrapper}>
              <span className={styles.text}>{agentFullName}</span>
              {(organisationName || data?.name) && (
                <span className={styles.subtext}>{organisationName ?? data?.name}</span>
              )}
            </div>
            {dynamicAgentVerificationFeatureFlag &&
              isStatusDynamic &&
              isOrgDataAvailable &&
              [AgentVerificationStatus.APPROVED, AgentVerificationStatus.REJECTED].includes(
                agentStatus as AgentVerificationStatus
              ) && (
                <Button variant="secondary" onClick={() => setOpenedModal(AgentVerificationModal.UPDATE_STATUS)}>
                  {t('dialog.updateAvStatus.title')}
                </Button>
              )}
            {[
              AgentVerificationStatus.PENDING,
              AgentVerificationStatus.PENDING_REVIEW,
              AgentVerificationStatus.PENDING_USER_VERIFICATION
            ].includes(agentStatus as AgentVerificationStatus) &&
              isOrgDataAvailable && (
                <div className={styles.buttonWrapper}>
                  {triggerEmailVerificationFeatureFlag &&
                    hasInboxVerificationEmailSentLog &&
                    [AgentVerificationStatus.PENDING, AgentVerificationStatus.PENDING_USER_VERIFICATION].includes(
                      agentStatus as AgentVerificationStatus
                    ) && (
                      <PercentButton
                        title={t('button.triggerEmailVerification')}
                        onClick={triggerEmailVerificationButton}
                        className={styles.triggerEmailVerificationButton}
                      />
                    )}
                  {agentStatus !== AgentVerificationStatus.PENDING_USER_VERIFICATION && (
                    <>
                      <PercentButton
                        title={t('button.reject')}
                        onClick={() => setOpenedModal(AgentVerificationModal.REJECT)}
                        className={styles.rejectButton}
                        testId="open-rejection-modal"
                      />
                      {canMarkAsSafe ? (
                        <Button size="small" onClick={handleMarkAsSafe} variant="reject">
                          {t('button.markAsSafe')}
                        </Button>
                      ) : (
                        <PercentButton
                          title={t('button.approve')}
                          onClick={() => setOpenedModal(AgentVerificationModal.APPROVE)}
                          className={styles.approveButton}
                        />
                      )}
                    </>
                  )}
                </div>
              )}
          </div>
        </Flex>
        {isOrgDataAvailable && <Tabs activeTab={activeTab} onChangeTab={setActiveTab} items={tabs} />}
      </CardDashboard>
      <AgentVerificationApproveModalContainer
        agentFullName={agentFullName}
        organisationName={organisationName}
        refresh={refresh}
      />
      <AgentVerificationRejectModalContainer
        agentFullName={agentFullName}
        organisationName={organisationName}
        refresh={refresh}
        allowedOrganisations={allowedOrganisations}
        partnerName={partnerName}
      />
      <AgentVerificationApproveEmailModal isLoading={isLoading} errorMessage={errorMessage} refresh={refresh} />
      {dynamicAgentVerificationFeatureFlag && (
        <AgentVerificationUpdateStatusModal
          agentFullName={agentFullName}
          organisationName={organisationName}
          agentVerificationStatus={agentStatus}
          refresh={refresh}
          allowedOrganisations={allowedOrganisations}
          partnerName={partnerName}
        />
      )}
      {isAutoFraudCheckEnabled && (
        <AgentVerificationMarkAsSafeModal
          id={id}
          fullName={agentFullName}
          organisationName={organisationName}
          refresh={markAsSafeRefresh}
        />
      )}
    </>
  )
}
